import React, { useState, useEffect } from "react";
import { Link, useParams, useLocation, useNavigate } from "react-router-dom";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import { FetchChatTrackerConversationAction } from "../../../Actions/AdminUser";
import ChatTrackerNav from "./ChatTrackerNav";

const ConversationDetail = ({
  fetchConversation,
  conversationData,
  loading,
}) => {
  const navigate = useNavigate();

  // Extract chat_id from URL parameters
  const { id: chatIdParam } = useParams();
  const chatId = parseInt(chatIdParam, 10);
  const location = useLocation();

  // If user_id was passed in location state, use it directly
  const userIdFromState = location.state?.userId;

  console.log("URL parameter (chat ID):", chatIdParam);
  console.log("User ID from location state:", userIdFromState);

  const [didAttemptFetch, setDidAttemptFetch] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");
  const [userId, setUserId] = useState(userIdFromState || null);
  const [user, setUser] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [selectedSession, setSelectedSession] = useState(null);
  const [messages, setMessages] = useState([]);
  const [messagesByDate, setMessagesByDate] = useState({});
  const [activeTab, setActiveTab] = useState("messages");
  const [sessionId, setSessionId] = useState(null);

  // Effect to redirect if we have no way to determine user ID
  useEffect(() => {
    // If we don't have a userId from route state, props, or anywhere else,
    // and we've already attempted a fetch, redirect back to the tracker home
    if (!userId && !userIdFromState && didAttemptFetch && !isLoading) {
      console.warn(
        "Cannot determine user ID. Redirecting to chat tracker home."
      );
      setErrorMessage(
        "Unable to determine the user ID for this conversation. Redirecting..."
      );

      // Set timeout to allow user to see the error message before redirect
      const timer = setTimeout(() => {
        navigate("/admin-dashboard/chat-tracker");
      }, 3000);

      return () => clearTimeout(timer);
    }
  }, [userId, userIdFromState, didAttemptFetch, isLoading, navigate]);

  // Fetch user sessions when component mounts or when userId changes
  useEffect(() => {
    const loadData = async () => {
      setIsLoading(true);

      // We must have a valid userId to fetch conversations
      if (!userId) {
        console.warn(
          "No valid user ID available yet. Will fetch once available."
        );
        setDidAttemptFetch(true);
        setIsLoading(false);
        return;
      }

      try {
        console.log("Fetching conversations for user ID:", userId);

        // Pass the explicit user_id to the action
        const result = await fetchConversation(userId);
        console.log("API fetch result:", result);
        setDidAttemptFetch(true);
        setIsLoading(false);
      } catch (error) {
        console.error("Error fetching conversation data:", error);
        setErrorMessage("Error loading conversation data. Please try again.");
        setDidAttemptFetch(true);
        setIsLoading(false);
      }
    };

    if (userId) {
      loadData();
    } else if (chatId && !userIdFromState) {
      // If we have a chat ID but no user ID, we need to redirect to get user info
      console.warn("Have chat ID but no user ID. Will need user list first.");
      setDidAttemptFetch(true);
      setIsLoading(false);
    }
  }, [fetchConversation, userId, chatId, userIdFromState]);

  // Process user and sessions data from Redux
  useEffect(() => {
    if (conversationData) {
      console.log("Raw conversation data:", conversationData);

      // Extract data from response
      const results = conversationData.results || conversationData || {};
      console.log("Results:", results);

      // Extract user data
      const userData = results.user || {};
      console.log("User data:", userData);

      // If we don't have a userId yet but the response includes it, save it
      if (!userId && userData.id) {
        console.log("Setting user ID from response:", userData.id);
        setUserId(userData.id);
      }

      // Extract sessions data
      const sessionsData = results.sessions || [];
      console.log("Sessions data:", sessionsData);

      // Set user and sessions state
      setUser(userData);
      setSessions(sessionsData);

      // If we have sessions, try to find the one matching our chat ID first
      if (sessionsData.length > 0) {
        let sessionToSelect;

        // If we have a chat ID from the URL, try to find the matching session
        if (chatId) {
          sessionToSelect = sessionsData.find((s) => s.id === chatId);
        }

        // If no matching session or no chat ID, select the first session
        if (!sessionToSelect) {
          sessionToSelect = sessionsData[0];
        }

        console.log("Selected session:", sessionToSelect);
        setSelectedSession(sessionToSelect);
        setSessionId(sessionToSelect.id);

        // Process messages for the selected session
        if (
          sessionToSelect.messages &&
          Array.isArray(sessionToSelect.messages)
        ) {
          processSessionMessages(sessionToSelect, userData);
        } else {
          console.warn("No messages array found in the selected session");
        }
      } else {
        console.warn("No sessions found in the response data");
      }
    } else {
      console.warn("No conversation data received from Redux");
    }
  }, [conversationData, chatId, userId]);

  // Function to process messages for a session
  const processSessionMessages = (session, userData) => {
    console.log("Processing messages for session:", session.id);

    if (!session.messages || !Array.isArray(session.messages)) {
      console.warn("No valid messages array in session:", session);
      setMessages([]);
      setMessagesByDate({});
      return;
    }

    const formattedMessages = session.messages.map((msg) => {
      console.log("Processing message:", msg);
      return {
        id: msg.id,
        sender: msg.role === "user" ? "user" : "bot",
        senderName:
          msg.role === "user"
            ? userData.username || userData.email
            : "AI Assistant",
        content: msg.content || "", // Ensure content is never null
        timestamp: msg.timestamp,
      };
    });

    console.log("Formatted messages:", formattedMessages);
    setMessages(formattedMessages);

    // Group messages by date
    const groupedMessages = {};
    formattedMessages.forEach((msg) => {
      const date = new Date(msg.timestamp).toLocaleDateString();
      if (!groupedMessages[date]) {
        groupedMessages[date] = [];
      }
      groupedMessages[date].push(msg);
    });

    console.log("Grouped messages by date:", groupedMessages);
    setMessagesByDate(groupedMessages);
  };

  // Handle session change
  const handleSessionChange = (session) => {
    setSelectedSession(session);
    setSessionId(session.id);

    if (session.messages && Array.isArray(session.messages)) {
      processSessionMessages(session, user);
    } else {
      setMessages([]);
      setMessagesByDate({});
    }
  };

  // Format date for display
  const formatDate = (dateString) => {
    const options = { year: "numeric", month: "long", day: "numeric" };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  // Format time for display
  const formatTime = (dateString) => {
    const options = { hour: "2-digit", minute: "2-digit" };
    return new Date(dateString).toLocaleTimeString(undefined, options);
  };

  if (isLoading) {
    return (
      <div className="bg-gray-100 min-h-screen">
        <ChatTrackerNav />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex justify-center items-center h-64">
            <svg
              className="animate-spin h-10 w-10 text-blue-500"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              ></circle>
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              ></path>
            </svg>
            <span className="ml-3 text-lg text-gray-600">
              Loading conversations...
            </span>
          </div>
        </div>
      </div>
    );
  }

  if (errorMessage) {
    return (
      <div className="bg-gray-100 min-h-screen">
        <ChatTrackerNav />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="bg-white shadow overflow-hidden sm:rounded-lg p-6">
            <div className="text-center py-12">
              <svg
                className="mx-auto h-12 w-12 text-red-400"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              </svg>
              <h3 className="mt-2 text-lg font-medium text-gray-900">Error</h3>
              <p className="mt-1 text-sm text-gray-500">{errorMessage}</p>
              <div className="mt-6">
                <Link
                  to="/admin-dashboard/chat-tracker"
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  Back to all users
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!user || sessions.length === 0) {
    return (
      <div className="bg-gray-100 min-h-screen">
        <ChatTrackerNav />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="bg-white shadow overflow-hidden sm:rounded-lg p-6">
            <div className="text-center py-12">
              <svg
                className="mx-auto h-12 w-12 text-gray-400"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <h3 className="mt-2 text-lg font-medium text-gray-900">
                No conversations found
              </h3>
              <p className="mt-1 text-sm text-gray-500">
                This user doesn't have any conversations or you don't have
                permission to view them.
              </p>
              <div className="mt-6">
                <Link
                  to="/admin-dashboard/chat-tracker"
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  Back to all users
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gray-100 min-h-screen">
      <ChatTrackerNav />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="mb-6">
          <Link
            to="/admin-dashboard/chat-tracker"
            className="inline-flex items-center text-sm font-medium text-blue-600 hover:text-blue-800"
          >
            <svg
              className="mr-2 h-5 w-5"
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 20 20"
              fill="currentColor"
              aria-hidden="true"
            >
              <path
                fillRule="evenodd"
                d="M7.707 14.707a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l2.293 2.293a1 1 0 010 1.414z"
                clipRule="evenodd"
              />
            </svg>
            Back to all users
          </Link>
        </div>

        {/* User Header */}
        <div className="bg-white shadow overflow-hidden sm:rounded-lg mb-6">
          <div className="px-4 py-5 sm:px-6 flex justify-between items-center">
            <div>
              <h2 className="text-lg leading-6 font-medium text-gray-900">
                Conversations with {user.username || user.email || "User"}
              </h2>
              <p className="mt-1 max-w-2xl text-sm text-gray-500">
                User ID: {user.id} · Total Sessions: {sessions.length}
              </p>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Session Selection */}
          <div className="md:col-span-1">
            <div className="bg-white shadow rounded-lg mb-6">
              <div className="px-4 py-5 border-b border-gray-200">
                <h3 className="text-lg font-medium leading-6 text-gray-900">
                  Sessions
                </h3>
              </div>
              <div className="divide-y divide-gray-200 max-h-96 overflow-y-auto">
                {sessions.map((session) => (
                  <button
                    key={session.id}
                    onClick={() => handleSessionChange(session)}
                    className={`w-full text-left px-4 py-3 transition-colors hover:bg-gray-50 ${
                      sessionId === session.id ? "bg-blue-50" : ""
                    }`}
                  >
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-medium text-gray-900">
                        Session #{session.id}
                      </span>
                      <span className="text-xs text-gray-500">
                        {session.message_count} messages
                      </span>
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      Started: {formatDate(session.started_at)}
                    </p>
                  </button>
                ))}
              </div>
            </div>

            {/* User Info Panel */}
            <div className="bg-white shadow rounded-lg">
              <div className="px-4 py-5 border-b border-gray-200">
                <h3 className="text-lg font-medium leading-6 text-gray-900">
                  User Information
                </h3>
              </div>
              <div className="px-4 py-5">
                <div className="space-y-4">
                  <div>
                    <h4 className="text-sm font-medium text-gray-500">Email</h4>
                    <p className="mt-1 text-sm text-gray-900">
                      {user.email || "N/A"}
                    </p>
                  </div>
                  {user.username && (
                    <div>
                      <h4 className="text-sm font-medium text-gray-500">
                        Username
                      </h4>
                      <p className="mt-1 text-sm text-gray-900">
                        {user.username}
                      </p>
                    </div>
                  )}
                  {user.first_name && user.last_name && (
                    <div>
                      <h4 className="text-sm font-medium text-gray-500">
                        Name
                      </h4>
                      <p className="mt-1 text-sm text-gray-900">
                        {user.first_name} {user.last_name}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Conversation Messages */}
          <div className="md:col-span-2">
            <div className="bg-white shadow rounded-lg">
              <div className="border-b border-gray-200">
                <nav className="-mb-px flex">
                  <button
                    onClick={() => setActiveTab("messages")}
                    className={`${
                      activeTab === "messages"
                        ? "border-blue-500 text-blue-600"
                        : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                    } whitespace-nowrap py-4 px-6 border-b-2 font-medium text-sm`}
                  >
                    Messages
                  </button>
                  <button
                    onClick={() => setActiveTab("analytics")}
                    className={`${
                      activeTab === "analytics"
                        ? "border-blue-500 text-blue-600"
                        : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                    } whitespace-nowrap py-4 px-6 border-b-2 font-medium text-sm`}
                  >
                    Analytics
                  </button>
                </nav>
              </div>
              {activeTab === "messages" && (
                <div className="p-4">
                  <div className="flow-root">
                    {selectedSession ? (
                      Object.keys(messagesByDate).length > 0 ? (
                        Object.entries(messagesByDate).map(
                          ([date, dayMessages]) => (
                            <div key={date} className="mb-6">
                              <div className="relative">
                                <div
                                  className="absolute inset-0 flex items-center"
                                  aria-hidden="true"
                                >
                                  <div className="w-full border-t border-gray-300"></div>
                                </div>
                                <div className="relative flex justify-center">
                                  <span className="px-3 bg-white text-sm text-gray-500">
                                    {formatDate(new Date(date))}
                                  </span>
                                </div>
                              </div>
                              <div className="mt-4 space-y-6">
                                {dayMessages.map((message) => (
                                  <div
                                    key={message.id}
                                    className={`flex ${
                                      message.sender === "user"
                                        ? "justify-end"
                                        : "justify-start"
                                    }`}
                                  >
                                    <div
                                      className={`max-w-md rounded-lg px-4 py-2 shadow ${
                                        message.sender === "user"
                                          ? "bg-blue-50 text-blue-800"
                                          : "bg-white border border-gray-200"
                                      }`}
                                    >
                                      <div className="flex items-center mb-1">
                                        <span className="font-medium text-xs">
                                          {message.senderName}
                                        </span>
                                        <span className="ml-2 text-xs text-gray-500">
                                          {formatTime(message.timestamp)}
                                        </span>
                                      </div>
                                      <div
                                        className="text-sm"
                                        dangerouslySetInnerHTML={{
                                          __html: message.content,
                                        }}
                                      />
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )
                        )
                      ) : (
                        <div className="text-center py-8">
                          <p className="text-gray-500">
                            No messages in this session.
                          </p>
                        </div>
                      )
                    ) : (
                      <div className="text-center py-8">
                        <p className="text-gray-500">
                          Select a session to view messages.
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              )}
              {activeTab === "analytics" && (
                <div className="p-4">
                  <div className="text-center py-8">
                    <p className="text-gray-500">
                      Analytics features coming soon.
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

ConversationDetail.propTypes = {
  fetchConversation: PropTypes.func.isRequired,
  conversationData: PropTypes.object,
  loading: PropTypes.bool,
};

const mapStateToProps = (state) => {
  console.log("Redux state:", state);
  console.log(
    "ChatTracker data:",
    state.adminUser?.chatTrackerConversationData
  );

  return {
    conversationData: state.adminUser?.chatTrackerConversationData,
    loading: state.adminUser?.loading,
  };
};

const mapDispatchToProps = {
  fetchConversation: FetchChatTrackerConversationAction,
};

export default connect(mapStateToProps, mapDispatchToProps)(ConversationDetail);
